import { locationApi } from "@api";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from ".";
import { DEFAULT_COUNTRY } from "@constants";
import { ILocation, ILocationTreeCountry, LocationState } from "@interfaces";

const initialState: LocationState = {
    country: [],
    isLoadingCountry: false,
    countryError: null,
    state: [],
    isLoadingState: false,
    stateError: null,
    region: [],
    isLoadingRegion: false,
    regionError: null,
    locality: [],
    isLoadingLocality: false,
    localityError: null,
    allLocations: [],
    isLoadingAllLocations: false,
    allLocationsError: null,
    selectedLocations: {
        country: DEFAULT_COUNTRY,
        state: null,
        region: null,
        locality: null,
    },
};

/**
 * Single FT call returning the full country -> state -> region -> locality
 * tree. Should be dispatched once on app bootstrap; the picker reads
 * derived lists from this tree instead of issuing four cascading TDMS
 * calls per interaction.
 */
export const fetchAllLocations = createAsyncThunk<
    ILocationTreeCountry[],
    void,
    { rejectValue: string }
>("location/fetchAllLocations", async (_void, { rejectWithValue }) => {
    try {
        const response: any = await locationApi.getAllLocations();
        // FT envelope: { success, code, data: ILocationTreeCountry[] }
        const data = response?.data?.data ?? response?.data ?? [];
        return data as ILocationTreeCountry[];
    } catch (error: any) {
        return rejectWithValue(
            error.response?.data?.message ?? "Failed to fetch all locations",
        );
    }
});

export const fetchCountries = createAsyncThunk<ILocation[], void, { rejectValue: string }>(
    "location/fetchCountries",
    async (_, { rejectWithValue }) => {
        try {
            const response = await locationApi.getCountries();
            return response.data.results as ILocation[];
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch country");
        }
    },
);

export const fetchStates = createAsyncThunk<ILocation[], string, { rejectValue: string }>(
    "location/fetchStates",
    async (countryId, { rejectWithValue }) => {
        try {
            const response = await locationApi.getStates(countryId);
            return response.data.results as ILocation[];
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message ?? "Failed to fetch state");
        }
    },
);

export const fetchRegions = createAsyncThunk<
    ILocation[],
    { stateId?: string; countryId?: string },
    { rejectValue: string }
>("location/fetchRegions", async ({ stateId, countryId }, { rejectWithValue }) => {
    try {
        const response = await locationApi.getRegions({ stateId, countryId });
        return response.data.results as ILocation[];
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message ?? "Failed to fetch region");
    }
});

export const fetchLocalities = createAsyncThunk<
    ILocation[],
    { regionId?: string; countryId?: string },
    { rejectValue: string }
>("location/fetchLocalities", async ({ regionId, countryId }, { rejectWithValue }) => {
    try {
        const response = await locationApi.getTowns({ regionId, countryId });
        return response.data.results as ILocation[];
    } catch (error: any) {
        return rejectWithValue(error.response?.data?.message ?? "Failed to fetch locality");
    }
});
const locationSearchSlice = createSlice({
    name: "locationSearch",
    initialState,
    reducers: {
        setSelectedLocations: (
            state,
            action: PayloadAction<{
                country?: ILocation | null;
                state?: ILocation | null;
                region?: ILocation | null;
                locality?: ILocation | null;
            }>,
        ) => {
            state.selectedLocations = {
                country: action.payload.country ?? null,
                state: action.payload.state ?? null,
                region: action.payload.region ?? null,
                locality: action.payload.locality ?? null,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCountries.pending, (state) => {
                state.isLoadingCountry = true;
                state.countryError = null;
            })
            .addCase(fetchCountries.fulfilled, (state, action: PayloadAction<ILocation[]>) => {
                state.isLoadingCountry = false;
                state.country = action.payload;
            })
            .addCase(
                fetchCountries.rejected,
                (state, action: PayloadAction<string | undefined>) => {
                    state.isLoadingCountry = false;
                    state.countryError = action.payload ?? "Failed to fetch country";
                },
            )
            .addCase(fetchStates.pending, (state) => {
                state.isLoadingState = true;
                state.stateError = null;
            })
            .addCase(fetchStates.fulfilled, (state, action: PayloadAction<ILocation[]>) => {
                state.isLoadingState = false;
                state.state = action.payload;
            })
            .addCase(fetchStates.rejected, (state, action: PayloadAction<string | undefined>) => {
                state.isLoadingState = false;
                state.stateError = action.payload ?? "Failed to fetch state";
            })
            .addCase(fetchRegions.pending, (state) => {
                state.isLoadingRegion = true;
                state.regionError = null;
            })
            .addCase(fetchRegions.fulfilled, (state, action: PayloadAction<ILocation[]>) => {
                state.isLoadingRegion = false;
                state.region = action.payload;
            })
            .addCase(fetchRegions.rejected, (state, action: PayloadAction<string | undefined>) => {
                state.isLoadingRegion = false;
                state.regionError = action.payload ?? "Failed to fetch region";
            })
            .addCase(fetchLocalities.pending, (state) => {
                state.isLoadingLocality = true;
                state.localityError = null;
            })
            .addCase(fetchLocalities.fulfilled, (state, action: PayloadAction<ILocation[]>) => {
                state.isLoadingLocality = false;
                state.locality = action.payload;
            })
            .addCase(
                fetchLocalities.rejected,
                (state, action: PayloadAction<string | undefined>) => {
                    state.isLoadingLocality = false;
                    state.localityError = action.payload ?? "Failed to fetch locality";
                },
            )
            .addCase(fetchAllLocations.pending, (state) => {
                state.isLoadingAllLocations = true;
                state.allLocationsError = null;
            })
            .addCase(
                fetchAllLocations.fulfilled,
                (state, action: PayloadAction<ILocationTreeCountry[]>) => {
                    state.isLoadingAllLocations = false;
                    state.allLocations = action.payload;
                },
            )
            .addCase(
                fetchAllLocations.rejected,
                (state, action: PayloadAction<string | undefined>) => {
                    state.isLoadingAllLocations = false;
                    state.allLocationsError =
                        action.payload ?? "Failed to fetch all locations";
                },
            );
    },
});
export const { setSelectedLocations } = locationSearchSlice.actions;
export const selectLocationSearch = (state: RootState) => state.locationSearch;

// `deriveLocationLists` is intentionally NOT re-exported here — it lives
// in `src/Utilities/locationTree.ts` and is imported directly from
// `@utilities`. Re-exporting from a Redux slice would create a circular
// import chain (slice -> @utilities barrel -> logoutUtils -> @redux).

export default locationSearchSlice.reducer;
